import {assertAndContinue} from 'Asserts';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {
  getModelConfig,
  SyncedBackboneTypes,
  SyncedGraphQLTypes,
  SyncedModelName,
} from 'graphql-client/backbone-graphql-sync/SyncedModels';

/**
 * If our types for a Backbone model are too strict and assume a key is always present or null, but it could
 * actually be undefined, we'll try to sync `undefined` to the Apollo store. Optional GQL fields get an
 * autogenerated TS type of foo | null, and `undefined` will not satisfy the field. Trying to read that model
 * from the store will fail with a difficult-to-debug error, so instead we change all undefined values to null
 * here and log it so we can fix the incorrect type.
 */
function nullifyUndefinedAttributes<K extends SyncedModelName>(
  modelName: K,
  attrs: SyncedGraphQLTypes[K]
): SyncedGraphQLTypes[K] {
  for (const [key, value] of Object.entries(attrs)) {
    if (value === undefined) {
      assertAndContinue(
        false,
        `Tried to sync key ${key}: undefined to the Apollo store from Backbone model ${modelName}. Likely indicates that our types are too strict.`
      );
      // @ts-expect-error key is valid index
      attrs[key] = null;
    }
  }
  return attrs;
}

/**
 * Given a SyncedModelName and a Backbone model, sync the model's attributes to GraphQL
 * by using its config. Intended to be called once during the model's `initialize` function
 * (where in `initialize` is up to the caller; concerns of calling before all relevant attributes
 * have been set are model-dependent). This assumes that all relevant changes will be signaled
 * by a `change` event on the Backbone model.
 *
 * TODO error if already called (would just result in double syncing but still)
 */
export function initBackboneToGraphQLSync<K extends SyncedModelName>(
  modelName: K,
  model: SyncedBackboneTypes[K]
): void {
  const modelConfig = getModelConfig(modelName);
  model.listenTo(model, 'change', () => {
    const attributes = nullifyUndefinedAttributes(modelName, modelConfig.getGraphQLAttrs(model));
    // We sometimes generate non-persisted backbones.  In this event, the id is null
    // and shouldn't be persisted to cache either.
    if (attributes.id) {
      writeFragmentsToCache<SyncedGraphQLTypes[K]>(modelConfig.fragmentWrapper, [attributes]);
    }
  });

  // Trigger a change event to sync the model initially; if this is added to the end of `initialize`,
  // it might not sync until the next change otherwise.
  model.trigger('change');
}
